import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { functionsBaseUrl, appCheckHeaders } from '../../firebase';
import AppBrandMark from '../AppBrandMark';
import MicrosoftButton from '../Security/MicrosoftButton';
import {
  isMfaRequiredError,
  resolverFromError,
  createInvisibleRecaptcha,
  sendSmsForResolver,
  resolveWithSms,
  resolveWithTotp,
  friendlyAuthError,
  isSsoRequiredEmail,
} from '../../services/careAuthService';
import { consumeSignOutReason } from '../../hooks/useSessionGuard';
import '../Security/Security.css';
import './LoginPage.css';

const SIGN_OUT_NOTICES = {
  idle: 'You were signed out after 90 minutes of inactivity. Sign in to continue.',
  'session-expired': 'Your session reached its 12-hour limit. Sign in to continue.',
  'mfa-enrolled': 'Two-step verification is set up. Sign in again and enter your verification code.',
  'microsoft-linked': 'Your Microsoft account is connected. Use "Sign in with Microsoft" from now on.',
};

const MFA_RECAPTCHA_ID = 'login-mfa-recaptcha';

export default function LoginPage() {
  const { signIn, signInWithMicrosoft } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState('');

  // Multi-factor step (Firebase MFA for external/email-password accounts)
  const [mfa, setMfa] = useState(null); // { resolver, hint, verificationId }
  const [mfaCode, setMfaCode] = useState('');
  const recaptchaRef = useRef(null);
  const passwordRef = useRef(null);

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');

  useEffect(() => {
    const reason = consumeSignOutReason();
    if (reason && SIGN_OUT_NOTICES[reason]) setNotice(SIGN_OUT_NOTICES[reason]);
    return () => {
      try { recaptchaRef.current?.clear(); } catch { /* ignore */ }
    };
  }, []);

  const getRecaptcha = () => {
    if (!recaptchaRef.current) recaptchaRef.current = createInvisibleRecaptcha(MFA_RECAPTCHA_ID);
    return recaptchaRef.current;
  };

  const beginMfa = async (err) => {
    const resolver = resolverFromError(err);
    const hints = resolver.hints || [];
    // Prefer TOTP when both exist (no SMS cost / delay); otherwise the first phone hint.
    const hint = hints.find((h) => h.factorId === 'totp') || hints[0];
    if (!hint) {
      setError('This account requires two-step verification but has no method available. Contact your administrator.');
      return;
    }
    if (hint.factorId === 'phone') {
      try {
        const verificationId = await sendSmsForResolver(resolver, hint, getRecaptcha());
        setMfa({ resolver, hint, verificationId });
      } catch (e) {
        try { recaptchaRef.current?.clear(); } catch { /* ignore */ }
        recaptchaRef.current = null;
        setError(friendlyAuthError(e, 'Could not send the verification text.'));
        return;
      }
    } else {
      setMfa({ resolver, hint, verificationId: null });
    }
    setMfaCode('');
  };

  const switchMfaHint = async (hint) => {
    setError('');
    if (hint.factorId === 'phone') {
      setSubmitting(true);
      try {
        const verificationId = await sendSmsForResolver(mfa.resolver, hint, getRecaptcha());
        setMfa({ ...mfa, hint, verificationId });
      } catch (e) {
        setError(friendlyAuthError(e, 'Could not send the verification text.'));
      } finally {
        setSubmitting(false);
      }
    } else {
      setMfa({ ...mfa, hint, verificationId: null });
    }
    setMfaCode('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    const trimmed = email.trim();
    if (!password) {
      // Staff who have already connected Microsoft should use the button; staff who
      // have NOT yet connected (microsoft_pending) must sign in with a password first.
      // Don't steer everyone to the button — a forgotten/stale password then loops
      // them between this message and "account already exists".
      setError(isSsoRequiredEmail(trimmed)
        ? 'Enter your password. If you have already connected your Microsoft account, use "Sign in with Microsoft" above. If you have not connected it yet (or forgot your password), sign in with your password — use "Forgot password?" to set a new one — and you will be prompted to connect Microsoft.'
        : 'Enter your password.');
      return;
    }
    setSubmitting(true);
    try {
      await signIn(trimmed, password);
    } catch (err) {
      if (isMfaRequiredError(err)) {
        await beginMfa(err);
      } else {
        setError(friendlyAuthError(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleMicrosoft = async () => {
    setError('');
    setNotice('');
    setSubmitting(true);
    try {
      await signInWithMicrosoft();
    } catch (err) {
      if (isMfaRequiredError(err)) {
        // Only possible if a Microsoft-linked account still has Firebase factors (e.g. break-glass).
        await beginMfa(err);
      } else if (err?.code === 'auth/account-exists-with-different-credential') {
        // Firebase account is still password-only (not yet linked). Put the user on
        // the password path: prefill the email Firebase reports and focus the field.
        const knownEmail = err?.customData?.email || err?.email;
        if (knownEmail && !email) setEmail(knownEmail);
        setError('This account has not been connected to Microsoft yet. Sign in below with your email and password (use "Forgot password?" if you do not remember it) — you will then be asked to connect your Microsoft account.');
        setTimeout(() => passwordRef.current?.focus(), 0);
      } else {
        setError(friendlyAuthError(err, 'Microsoft sign-in failed.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (mfaCode.trim().length < 6) {
      setError('Enter the 6-digit verification code.');
      return;
    }
    setSubmitting(true);
    try {
      if (mfa.hint.factorId === 'phone') {
        await resolveWithSms(mfa.resolver, mfa.verificationId, mfaCode);
      } else {
        await resolveWithTotp(mfa.resolver, mfa.hint, mfaCode);
      }
      // onAuthStateChanged takes over from here.
    } catch (err) {
      setError(friendlyAuthError(err, 'Verification failed.'));
    } finally {
      setSubmitting(false);
    }
  };

  const cancelMfa = () => {
    setMfa(null);
    setMfaCode('');
    setError('');
    setPassword('');
  };

  if (mfa) {
    const isPhone = mfa.hint.factorId === 'phone';
    const others = (mfa.resolver.hints || []).filter((h) => h.uid !== mfa.hint.uid);
    return (
      <div className="login-page">
        <div className="login-card">
          <h1 className="login-title"><AppBrandMark size="login" /></h1>
          <h3 className="login-forgot-title">Two-step verification</h3>
          <p className="login-forgot-desc">
            {isPhone
              ? <>Enter the 6-digit code we texted to {mfa.hint.phoneNumber || 'your phone'}.</>
              : <>Enter the 6-digit code from your authenticator app{mfa.hint.displayName ? ` (${mfa.hint.displayName})` : ''}.</>}
          </p>
          <form onSubmit={handleMfaSubmit} className="login-form">
            {error && <div className="login-error" role="alert">{error}</div>}
            <label className="login-label">
              Verification code
              <input
                className="login-input sec-input--code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                required
                autoFocus
              />
            </label>
            <button type="submit" className="login-submit" disabled={submitting}>
              {submitting ? 'Verifying…' : 'Verify and sign in'}
            </button>
            <div className="login-forgot-actions">
              {isPhone && (
                <button type="button" className="login-forgot-cancel" onClick={() => switchMfaHint(mfa.hint)} disabled={submitting}>
                  Resend code
                </button>
              )}
              {others.map((h) => (
                <button key={h.uid} type="button" className="login-forgot-cancel" onClick={() => switchMfaHint(h)} disabled={submitting}>
                  {h.factorId === 'phone' ? `Text a code to ${h.phoneNumber || 'my phone'} instead` : 'Use my authenticator app instead'}
                </button>
              ))}
              <button type="button" className="login-forgot-cancel" onClick={cancelMfa} disabled={submitting}>
                Cancel
              </button>
            </div>
          </form>
          <div id={MFA_RECAPTCHA_ID} />
        </div>
      </div>
    );
  }

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotMessage('');
    const trimEmail = forgotEmail.trim();
    if (!trimEmail || !trimEmail.includes('@')) {
      setForgotError('Enter a valid email address.');
      return;
    }
    if (!functionsBaseUrl) {
      setForgotError('Password reset is not configured.');
      return;
    }
    setForgotSubmitting(true);
    try {
      const res = await fetch(`${functionsBaseUrl}/requestCarePasswordReset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await appCheckHeaders()) },
        body: JSON.stringify({ email: trimEmail }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setForgotError(data?.error?.message || 'Request failed. Try again.');
        return;
      }
      setForgotMessage(data?.result?.message || 'If an account exists for this email, a password reset link is on its way.');
      setForgotEmail('');
      setShowForgotPassword(false);
      setTimeout(() => setForgotMessage(''), 12000);
    } catch (err) {
      const msg = err.message || 'Request failed. Try again.';
      if (msg === 'Failed to fetch') {
        // Probe whether the endpoint exists (GET returns 405 if function is deployed)
        let hint = 'VPN or restrictive networks can block this.';
        try {
          const probe = await fetch(`${functionsBaseUrl}/requestCarePasswordReset`, { method: 'GET' });
          if (probe.status === 405) {
            hint = 'The service is reachable but the request failed. Try again; if it persists, try a different browser or network.';
          } else if (probe.status === 404) {
            hint = 'The password reset function may not be deployed. Ask your administrator to run "firebase deploy --only functions" and confirm the Firebase project ID matches this app.';
          }
        } catch {
          hint = 'The service could not be reached. VPN or restrictive networks can block it, or the Cloud Function may not be deployed. Try a different network, or ask your administrator to run "firebase deploy --only functions" and confirm the Firebase project ID.';
        }
        const endpoint = `${functionsBaseUrl}/requestCarePasswordReset`;
        setForgotError(
          `Network error: the password reset service could not be reached. ${hint} (Endpoint: ${endpoint})`
        );
      } else {
        setForgotError(msg);
      }
    } finally {
      setForgotSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <h1 className="login-title">
          <AppBrandMark size="login" />
        </h1>
        {forgotMessage && (
          <div className="login-forgot-success" role="status">
            {forgotMessage}
          </div>
        )}
        {notice && <div className="login-notice" role="status">{notice}</div>}
        <MicrosoftButton onClick={handleMicrosoft} disabled={submitting} />
        <p className="login-forgot-desc" style={{ textAlign: 'center', marginTop: '0.5rem' }}>
          Trilogy staff: use your Microsoft work account.
        </p>
        <div className="login-divider">or sign in with email</div>
        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="login-error" role="alert">{error}</div>}
          <label className="login-label">
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              className="login-input"
              placeholder="you@example.com"
            />
          </label>
          <label className="login-label">
            Password
            <input
              ref={passwordRef}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="login-input"
              placeholder="••••••••"
            />
          </label>
          <button type="submit" className="login-submit" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
          <p className="login-forgot-wrap">
            <button
              type="button"
              className="login-forgot-link"
              onClick={() => { setShowForgotPassword(true); setForgotError(''); setForgotMessage(''); setForgotEmail(email); }}
            >
              Forgot password?
            </button>
          </p>
        </form>

        {showForgotPassword && (
          <div className="login-forgot-panel">
            <h3 className="login-forgot-title">Request password reset</h3>
            <p className="login-forgot-desc">Enter your email and we’ll send you a link to choose a new password. Your current password keeps working until you use the link.</p>
            {forgotError && <div className="login-error" role="alert">{forgotError}</div>}
            <form onSubmit={handleForgotSubmit} className="login-form">
              <label className="login-label">
                Email
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  autoComplete="email"
                  required
                  className="login-input"
                  placeholder="you@example.com"
                  disabled={forgotSubmitting}
                />
              </label>
              <div className="login-forgot-actions">
                <button type="submit" className="login-submit" disabled={forgotSubmitting}>
                  {forgotSubmitting ? 'Sending…' : 'Email me a reset link'}
                </button>
                <button
                  type="button"
                  className="login-forgot-cancel"
                  onClick={() => { setShowForgotPassword(false); setForgotError(''); setForgotMessage(''); }}
                  disabled={forgotSubmitting}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
        <div id={MFA_RECAPTCHA_ID} />
      </div>
    </div>
  );
}
